import {useEffect, useState} from "react";
import {Controller, useForm} from "react-hook-form";
import {useDispatch, useSelector} from "react-redux";
import {yupResolver} from "@hookform/resolvers/yup";
import {css} from "@panda/css";
import {FetchBaseQueryError} from "@reduxjs/toolkit/query";
import * as Yup from "yup";

import {Button} from "@pg-design/button-module";
import {Input} from "@pg-design/inputs-module";
import {Link} from "@pg-design/link-module";
import {Loader} from "@pg-design/loader-module";
import {IPhoneFieldValue, PhoneField} from "@pg-design/phone-input-module";
import {Text} from "@pg-design/text-module";
import {notifyBugsnagClient} from "@pg-mono/bugsnag-client";
import {rpAppLink} from "@pg-mono/rp-routes";

import {IRPStore} from "../../app/rp_reducer";
import {useCreateFinancialApplicationMutation} from "../../application/api/post_financial_application";
import {buildRpFinancialApplicationPayload} from "../../application/utils/build_rp_financial_application_payload";
import {toJsonValues} from "../../form/translate/to_json_values";
import {validationMessages} from "../../form/utils/validation_messages";
import {addWarningNotification} from "../../notifications/utils/add_warning_notification";
import {formFields, formValuesType, useCalculatorFormInitialValues} from "../hooks/use_calculator_form_initial_values";
import {useCreditPaywall} from "../hooks/use_credit_paywall";
import {ICreditData, ICreditRatingData} from "../types/ICreditData";

const validationSchema = Yup.object({
    email: Yup.string().required(validationMessages.required).email(validationMessages.email),
    name: Yup.string().required(validationMessages.required),
    phone: Yup.object({
        code: Yup.number().required(validationMessages.required),
        number: Yup.string().required(validationMessages.required)
    })
});

interface IProps {
    submitData: ICreditData | ICreditRatingData;
    onAfterSend: (isSuccess: boolean) => void;
}

const privacyPolicyLink = rpAppLink.external.getHomeFinance.privacyPolicy;

export const CreditCalculatorForm = (props: IProps) => {
    const {areOffersUnlocked} = useCreditPaywall();
    const codes = useSelector((store: IRPStore) => store.variables.data?.country_codes);
    const initialValues = useCalculatorFormInitialValues();

    const [post, data] = useCreateFinancialApplicationMutation();
    const [sending, setSending] = useState(false);

    const dispatch = useDispatch();
    const formMethods = useForm<formValuesType>({defaultValues: initialValues, resolver: yupResolver(validationSchema)});

    const {handleSubmit, control, formState, reset} = formMethods;
    const {errors} = formState;

    useEffect(() => {
        reset(initialValues);
    }, [initialValues]);

    useEffect(() => {
        if (data.status === "rejected") {
            const error = data.error as FetchBaseQueryError;
            notifyBugsnagClient({message: error.status.toString()}, "CreditCalculatorForm useCreateFinancialApplicationMutation error");
            dispatch(addWarningNotification("Twoje zapytanie nie zostało wysłane. Sprawdź podane dane lub spróbuj ponownie później."));
            setSending(false);
        }

        if (data.status === "fulfilled") {
            props.onAfterSend(true);
        }
    }, [data]);

    const onSubmit = (values: formValuesType) => {
        setSending(true);
        const parsedValues = toJsonValues(formFields, values);
        const payload = buildRpFinancialApplicationPayload({
            name: parsedValues.name,
            email: parsedValues.email,
            phone: values.phone,
            submitData: props.submitData
        });
        post(payload);
    };

    const renderError = (message?: string) => {
        return (
            <Text variant="info_txt_1" className={errorMessageStyle}>
                {message}
            </Text>
        );
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <Controller
                name="name"
                control={control}
                render={({field}) => <Input placeholder="Imię i nazwisko" {...field} fieldState={errors.name && "error"} className={inputMarginStyle} />}
            />
            {errors.name && renderError(errors.name.message)}
            <Controller
                name="email"
                control={control}
                render={({field}) => <Input placeholder="Email" {...field} fieldState={errors.email && "error"} className={inputMarginStyle} />}
            />
            {errors.email && renderError(errors.email.message)}
            <div className={inputMarginStyle}>
                <Controller
                    name="phone"
                    control={control}
                    render={({field: {onChange, value, name}}) => {
                        const onValueChange = (field: string, value: IPhoneFieldValue) => {
                            onChange(value);
                        };

                        return (
                            <PhoneField
                                codes={codes || []}
                                onChange={onValueChange}
                                onAfterChange={() => null}
                                value={value}
                                name={name}
                                placeholder="Numer telefonu"
                                fieldState={(errors.phone?.code || errors.phone?.number) && "error"}
                            />
                        );
                    }}
                />
            </div>
            {errors.phone?.code && renderError(errors.phone.code.message)}
            {errors.phone?.number && renderError(errors.phone.number.message)}

            {areOffersUnlocked ? (
                <>
                    <Button type="submit" variant="filled_primary" className={buttonStyle}>
                        {sending ? <Loader size="sm" /> : "Wyślij"}
                    </Button>

                    <Text variant="info_txt_1" align="center">
                        Administratorem Twoich danych osobowych jest Property Group sp. z o.o.
                        <br />
                        <Link size="x-small" underline href={privacyPolicyLink} target="_blank">
                            Dowiedz się więcej o przetwarzaniu Twoich danych.
                        </Link>{" "}
                    </Text>
                </>
            ) : (
                <>
                    <Button type="submit" variant="filled_primary" className={buttonStyle}>
                        {sending ? <Loader size="sm" /> : "Zobacz ratę kredytu"}
                    </Button>

                    <Text variant="info_txt_1" align="center">
                        Kliknięcie przycisku „Zobacz ratę kredytu” oznacza zgodę na przetwarzanie podanych danych osobowych przez GetHome Finance sp. z o.o.
                        (dawniej PGF sp. z o.o.) z siedzibą w Warszawie w celu realizacji czynności pośrednictwa kredytu hipotecznego. Zgoda jest dobrowolna i
                        może zostać w każdej chwili odwołana.
                        <br />
                        <Link size="x-small" underline href={privacyPolicyLink} target="_blank">
                            Zasady przetwarzania danych osobowych
                        </Link>
                    </Text>
                </>
            )}
        </form>
    );
};

const errorMessageStyle = css({
    color: "var(--colors-danger)",
    width: "100%",
    textAlign: "right",
    marginBottom: 1.5
});

const inputMarginStyle = css({
    marginBottom: 1.5
});

const buttonStyle = css({
    width: "100%",
    marginBottom: 1.5
});
import {useEffect, useState} from "react";
import {Controller, useForm} from "react-hook-form";
import {useDispatch, useSelector} from "react-redux";
import {yupResolver} from "@hookform/resolvers/yup";
import {css} from "@panda/css";
import {FetchBaseQueryError} from "@reduxjs/toolkit/query";
import * as Yup from "yup";

import {Button} from "@pg-design/button-module";
import {Input} from "@pg-design/inputs-module";
import {Link} from "@pg-design/link-module";
import {Loader} from "@pg-design/loader-module";
import {IPhoneFieldValue, PhoneField} from "@pg-design/phone-input-module";
import {Text} from "@pg-design/text-module";
import {notifyBugsnagClient} from "@pg-mono/bugsnag-client";
import {rpAppLink} from "@pg-mono/rp-routes";

import {IRPStore} from "../../app/rp_reducer";
import {useCreateFinancialApplicationMutation} from "../../application/api/post_financial_application";
import {buildRpFinancialApplicationPayload} from "../../application/utils/build_rp_financial_application_payload";
import {toJsonValues} from "../../form/translate/to_json_values";
import {validationMessages} from "../../form/utils/validation_messages";
import {addWarningNotification} from "../../notifications/utils/add_warning_notification";
import {formFields, formValuesType, useCalculatorFormInitialValues} from "../hooks/use_calculator_form_initial_values";
import {useCreditPaywall} from "../hooks/use_credit_paywall";
import {ICreditData, ICreditRatingData} from "../types/ICreditData";

const validationSchema = Yup.object({
    email: Yup.string().required(validationMessages.required).email(validationMessages.email),
    name: Yup.string().required(validationMessages.required),
    phone: Yup.object({
        code: Yup.number().required(validationMessages.required),
        number: Yup.string().required(validationMessages.required)
    })
});

interface IProps {
    submitData: ICreditData | ICreditRatingData;
    onAfterSend: (isSuccess: boolean) => void;
}

const privacyPolicyLink = rpAppLink.external.getHomeFinance.privacyPolicy;

export const CreditCalculatorForm = (props: IProps) => {
    const {areOffersUnlocked} = useCreditPaywall();
    const codes = useSelector((store: IRPStore) => store.variables.data?.country_codes);
    const initialValues = useCalculatorFormInitialValues();

    const [post, data] = useCreateFinancialApplicationMutation();
    const [sending, setSending] = useState(false);

    const dispatch = useDispatch();
    const formMethods = useForm<formValuesType>({defaultValues: initialValues, resolver: yupResolver(validationSchema)});

    const {handleSubmit, control, formState, reset} = formMethods;
    const {errors} = formState;

    useEffect(() => {
        reset(initialValues);
    }, [initialValues]);

    useEffect(() => {
        if (data.status === "rejected") {
            const error = data.error as FetchBaseQueryError;
            notifyBugsnagClient({message: error.status.toString()}, "CreditCalculatorForm useCreateFinancialApplicationMutation error");
            dispatch(addWarningNotification("Twoje zapytanie nie zostało wysłane. Sprawdź podane dane lub spróbuj ponownie później."));
            setSending(false);
        }

        if (data.status === "fulfilled") {
            props.onAfterSend(true);
        }
    }, [data]);

    const onSubmit = (values: formValuesType) => {
        setSending(true);
        const parsedValues = toJsonValues(formFields, values);
        const payload = buildRpFinancialApplicationPayload({
            name: parsedValues.name,
            email: parsedValues.email,
            phone: values.phone,
            submitData: props.submitData
        });
        post(payload);
    };

    const renderError = (message?: string) => {
        return (
            <Text variant="info_txt_1" className={errorMessageStyle}>
                {message}
            </Text>
        );
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <Controller
                name="name"
                control={control}
                render={({field}) => <Input placeholder="Imię i nazwisko" {...field} fieldState={errors.name && "error"} className={inputMarginStyle} />}
            />
            {errors.name && renderError(errors.name.message)}
            <Controller
                name="email"
                control={control}
                render={({field}) => <Input placeholder="Email" {...field} fieldState={errors.email && "error"} className={inputMarginStyle} />}
            />
            {errors.email && renderError(errors.email.message)}
            <div className={inputMarginStyle}>
                <Controller
                    name="phone"
                    control={control}
                    render={({field: {onChange, value, name}}) => {
                        const onValueChange = (field: string, value: IPhoneFieldValue) => {
                            onChange(value);
                        };

                        return (
                            <PhoneField
                                codes={codes || []}
                                onChange={onValueChange}
                                onAfterChange={() => null}
                                value={value}
                                name={name}
                                placeholder="Numer telefonu"
                                fieldState={(errors.phone?.code || errors.phone?.number) && "error"}
                            />
                        );
                    }}
                />
            </div>
            {errors.phone?.code && renderError(errors.phone.code.message)}
            {errors.phone?.number && renderError(errors.phone.number.message)}

            {areOffersUnlocked ? (
                <>
                    <Button type="submit" variant="filled_primary" className={buttonStyle}>
                        {sending ? <Loader size="sm" /> : "Wyślij"}
                    </Button>

                    <Text variant="info_txt_1" align="center">
                        Administratorem Twoich danych osobowych jest Property Group sp. z o.o.
                        <br />
                        <Link size="x-small" underline href={privacyPolicyLink} target="_blank">
                            Dowiedz się więcej o przetwarzaniu Twoich danych.
                        </Link>{" "}
                    </Text>
                </>
            ) : (
                <>
                    <Button type="submit" variant="filled_primary" className={buttonStyle}>
                        {sending ? <Loader size="sm" /> : "Zobacz ratę kredytu"}
                    </Button>

                    <Text variant="info_txt_1" align="center">
                        Kliknięcie przycisku „Zobacz ratę kredytu” oznacza zgodę na przetwarzanie podanych danych osobowych przez GetHome Finance sp. z o.o.
                        (dawniej PGF sp. z o.o.) z siedzibą w Warszawie w celu realizacji czynności pośrednictwa kredytu hipotecznego. Zgoda jest dobrowolna i
                        może zostać w każdej chwili odwołana.
                        <br />
                        <Link size="x-small" underline href={privacyPolicyLink} target="_blank">
                            Zasady przetwarzania danych osobowych
                        </Link>
                    </Text>
                </>
            )}
        </form>
    );
};

const errorMessageStyle = css({
    color: "var(--colors-danger)",
    width: "100%",
    textAlign: "right",
    marginBottom: 1.5
});

const inputMarginStyle = css({
    marginBottom: 1.5
});

const buttonStyle = css({
    width: "100%",
    marginBottom: 1.5
});
